import type { NoteDTO } from "../../../api/api";
import { przezmorzeApi, throwMessageOnBadRequestStatus } from "../../../api/api";
import type {
  CreateOrUpdateProviderRequest as GeneratedCreateOrUpdateProviderRequest,
  ParticipantListRequiredFields,
  Provider as GeneratedProvider,
  ProviderAttachment,
  ProviderListItem,
  ProviderListResponse,
  ProviderPriceMargin,
} from "@przezmorze/api-types";

export type {
  ParticipantListRequiredFields,
  ProviderListItem as ProviderListResponseItem,
  ProviderPriceMargin as PriceMargin,
};
export type AttachmentResponse = ProviderAttachment;

// The generated schema types productType as a plain string union (openapi-typescript cannot emit a runtime
// enum), but this enum's members are used as runtime values throughout the codebase (icons, filters, grouping).
// Keep a real TS enum here — structurally identical to the generated string union — and use it as the source
// of truth for productType on both the response and request shapes.
export enum ProviderProductType {
  WATERBORNE_TRANSPORT_VAT_NP = "WATERBORNE_TRANSPORT_VAT_NP",
  WATERBORNE_TRANSPORT_VAT_0 = "WATERBORNE_TRANSPORT_VAT_0",
  HOTEL = "HOTEL",
  HOTEL_VAT_NP = "HOTEL_VAT_NP",
  ATTRACTION = "ATTRACTION",
  PLANE_TICKET_VAT_0 = "PLANE_TICKET_VAT_0",
  PLANE_TICKET_VAT_NP = "PLANE_TICKET_VAT_NP",
  BUS_TRANSPORT_VAT_NP = "BUS_TRANSPORT_VAT_NP",
  BUS_TRANSPORT_VAT_8 = "BUS_TRANSPORT_VAT_8",
}

export type ProviderResponse = Omit<GeneratedProvider, "productType"> & { readonly productType?: ProviderProductType };
export type CreateOrUpdateProviderRequest = Omit<GeneratedCreateOrUpdateProviderRequest, "productType"> & {
  readonly productType?: ProviderProductType;
};

export async function getProvider(providerId: string, abortSignal?: AbortSignal): Promise<ProviderResponse | null> {
  if (!providerId) {
    return null;
  }
  const response = await przezmorzeApi.get<ProviderResponse>(`/providers/${providerId}`, { signal: abortSignal });
  return response.data;
}

export async function listProviders(abortSignal?: AbortSignal): Promise<ProviderListItem[]> {
  const response = await przezmorzeApi.get<ProviderListResponse>("/providers", {
    signal: abortSignal,
  });
  return response.data.items;
}

export async function createProvider(
  request: CreateOrUpdateProviderRequest,
  abortSignal?: AbortSignal,
): Promise<ProviderResponse> {
  try {
    const response = await przezmorzeApi.post<ProviderResponse>("/providers", request, { signal: abortSignal });
    return response.data;
  } catch (error) {
    throwMessageOnBadRequestStatus(error);
  }
}

export async function updateProvider(
  providerId: string,
  request: CreateOrUpdateProviderRequest,
  abortSignal?: AbortSignal,
): Promise<ProviderResponse> {
  try {
    const response = await przezmorzeApi.put<ProviderResponse>(`/providers/${providerId}`, request, {
      signal: abortSignal,
    });
    return response.data;
  } catch (error) {
    throwMessageOnBadRequestStatus(error);
  }
}

export async function deleteProvider(providerId: string, abortSignal?: AbortSignal) {
  await przezmorzeApi.delete(`/providers/${providerId}`, { signal: abortSignal });
}

export async function addProviderAttachment(
  providerId: string,
  body: FormData,
  abortSignal?: AbortSignal,
): Promise<AttachmentResponse> {
  const response = await przezmorzeApi.post<AttachmentResponse>(`/providers/${providerId}/attachments`, body, {
    signal: abortSignal,
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data;
}

export async function deleteProviderAttachment(
  providerId: string,
  name: string,
  abortSignal?: AbortSignal,
): Promise<AttachmentResponse[]> {
  const response = await przezmorzeApi.delete<{ items: AttachmentResponse[] }>(
    `/providers/${providerId}/attachments/${name}`,
    {
      signal: abortSignal,
    },
  );
  return response.data.items;
}

export async function addListTemplate(
  providerId: string,
  body: FormData,
  abortSignal?: AbortSignal,
): Promise<ProviderResponse> {
  const response = await przezmorzeApi.put<ProviderResponse>(`/providers/${providerId}/list-template`, body, {
    signal: abortSignal,
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data;
}

export async function deleteListTemplate(providerId: string, abortSignal?: AbortSignal): Promise<ProviderResponse> {
  const response = await przezmorzeApi.delete<ProviderResponse>(`/providers/${providerId}/list-template`, {
    signal: abortSignal,
  });
  return response.data;
}

export async function updateProviderNotes(
  providerId: string,
  notes: NoteDTO[],
  abortSignal?: AbortSignal,
): Promise<ProviderResponse> {
  const response = await przezmorzeApi.put<ProviderResponse>(
    `/providers/${providerId}/notes`,
    { notes },
    {
      signal: abortSignal,
    },
  );
  return response.data;
}
